import React, { useEffect, useState } from "react";
import DragBox from "../../DragBox/Drag/dragBox.component.jsx";
import { useGlobalContext } from "../../../Context/global.context.jsx";
import { fetchUsers } from "../../../Context/functions.jsx";

const USER_ROLES = ["SUPER_ADMIN", "ADMIN", "PHARMACIST", "GENERAL"];
const USER_GRID_COLUMNS = "1.05fr 1.05fr 1.15fr 140px 86px .9fr .9fr 72px";

export default function Users() {
    const [users, setUsers] = useState([]);                // array of user rows from server
    const [editing, setEditing] = useState({});            // { [id]: { ...editedFields } }
    const [newUser, setNewUser] = useState({
        userName: "",
        password: "",
        email: "",
        dayOfWeek: "",
        ipAddress: "",
        patientTable: "",
        historyTable: "",
        role: "GENERAL",
        pharmacyID: "",
    });

    const { selectedTopButtons, setSelectedTopButtons } = useGlobalContext();
    const API_ENDPOINT = "https://gdmt.ca/PHP/special.php";

    // Load users on mount
    useEffect(() => {
        (async () => {
            try {
                const list = await fetchUsers();
                setUsers(Array.isArray(list) ? list : []);
            } catch (err) {
                console.error("Failed to load users", err);
            }
        })();
    }, []);

    // ---- helpers -------------------------------------------------------------

    // Grab the “current” value for a given field on a given row (merge local edits over original)
    const fieldValue = (row, field) =>
        editing[row.id]?.[field] ?? row?.[field] ?? (field === "role" ? "GENERAL" : "");

    // Determine if any of the editable fields differ from original for this row
    const isRowEdited = (row) => {
        const fields = ["userName", "password", "email", "role", "pharmacyID"];
        return fields.some((f) => {
            const orig = (row?.[f] ?? (f === "role" ? "GENERAL" : "")).toString().trim();
            const cur = (fieldValue(row, f) ?? "").toString().trim();
            return orig !== cur;
        });
    };

    const canInsert = (newUser.userName || "").trim().length > 0 && (newUser.password || "").trim().length > 0;
    const insertBtnClass = canInsert ? "btn btn-purple" : "btn btn-outline-purple";

    const closeBox = (box) => () => {
        const updated = (selectedTopButtons || []).filter((b) => b !== box);
        setSelectedTopButtons?.(updated);
    };

    // ---- API helpers ---------------------------------------------------------

    async function saveUser(row) {
        if (!row?.id) return;
        if (!isRowEdited(row)) return;

        const payload = {
            script: "saveUser",
            theType: "update",
            id: row.id,
            userName: fieldValue(row, "userName"),
            password: fieldValue(row, "password"),
            email: fieldValue(row, "email"),
            role: fieldValue(row, "role"),
            pharmacyID: fieldValue(row, "pharmacyID"),
        };

        try {
            const resp = await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await resp.json();
            if (data?.success) {
                // Refresh list from source of truth
                const list = await fetchUsers();
                console.log("Fetched users:", list);
                setUsers(Array.isArray(list) ? list : []);
                // Clear edits for this row
                setEditing((prev) => {
                    const next = { ...prev };
                    delete next[row.id];
                    return next;
                });
            } else {
                console.warn("Failed to save user.");
            }
        } catch (e) {
            console.error("Error saving user:", e);
        }
    }

    async function createUser() {
        if (!canInsert) return;

        const payload = {
            script: "saveUser",
            theType: "create",
            userName: (newUser.userName || "").trim(),
            password: (newUser.password || "").trim(),
            email: (newUser.email || "").trim(),
            role: newUser.role || "GENERAL",
            pharmacyID: (newUser.pharmacyID || "").trim(),
        };
        try {
            const resp = await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await resp.json();
            if (data?.success) {
                // Refresh list from source of truth
                const list = await fetchUsers();
                console.log("Fetched users:", list);
                setUsers(Array.isArray(list) ? list : []);
               setNewUser({
                    userName: "",
                    password: "",
                    email: "",
                    dayOfWeek: "",
                    ipAddress: "",
                    patientTable: "",
                    historyTable: "",
                    role: "GENERAL",
                    pharmacyID: "",
                });
            } else {
                console.warn("Failed to save user.");
            }
        } catch (e) {
            console.error("Error saving user:", e);
        }
    }

    // ---- render --------------------------------------------------------------

    return (
        <DragBox
            id="USERS_BOX"
            storageKey="USERS_BOX_POSITION"
            defaultPos={{ x: 320, y: 360 }}
            title="Users"
            width={1080}
            onAdd={null}
            zIndex={2050}
            addNote="-"
            onClose={closeBox("users")}
        >
            <div style={{ padding: 12, minWidth: 940 }}>
                <div style={{ marginBottom: 10 }}>
                    <strong>Existing users</strong>
                </div>

                {/* header row */}
                <div
                    className="border-bottom pb-1 mb-2 d-grid gap-2 align-items-center"
                    style={{ gridTemplateColumns: USER_GRID_COLUMNS }}
                >
                    <div className="ps-1 fw-bold small">Username</div>
                    <div className="ps-1 fw-bold small">Password</div>
                    <div className="ps-1 fw-bold small">Email</div>
                    <div className="ps-1 fw-bold small">Role</div>
                    <div className="ps-1 fw-bold small">Pharmacy ID</div>
                    <div className="ps-1 fw-bold small">Patient Table</div>
                    <div className="ps-1 fw-bold small">History Table</div>
                    <div className="flex-grow-1 fw-bold small">Save</div>
                </div>

                {/* rows */}
                {users.length === 0 && <div>No users found.</div>}
                {users.map((row) => {
                    const edited = isRowEdited(row);
                    const saveBtnClass = edited ? "btn btn-success" : "btn btn-outline-success";

                    return (
                        <div
                            key={row.id}
                            className="d-grid gap-2 mb-2 align-items-center"
                            style={{ gridTemplateColumns: USER_GRID_COLUMNS }}
                        >
                            <input
                                className="fs-7"
                                value={fieldValue(row, "userName")}
                                onChange={(e) =>
                                    setEditing((s) => ({ ...s, [row.id]: { ...s[row.id], userName: e.target.value } }))
                                }
                                style={{ padding: "6px 8px" }}
                            />
                            <input
                                className="fs-7"
                                type="text"
                                value={fieldValue(row, "password")}
                                onChange={(e) =>
                                    setEditing((s) => ({ ...s, [row.id]: { ...s[row.id], password: e.target.value } }))
                                }
                                style={{ padding: "6px 8px" }}
                            />
                            <input
                                className="fs-7"
                                type="email"
                                value={fieldValue(row, "email")}
                                onChange={(e) =>
                                    setEditing((s) => ({ ...s, [row.id]: { ...s[row.id], email: e.target.value } }))
                                }
                                style={{ padding: "6px 8px" }}
                            />
                            <select
                                className="fs-7"
                                value={fieldValue(row, "role")}
                                onChange={(e) =>
                                    setEditing((s) => ({ ...s, [row.id]: { ...s[row.id], role: e.target.value } }))
                                }
                                style={{ padding: "6px 8px" }}
                            >
                                {USER_ROLES.map((role) => (
                                    <option key={role} value={role}>{role}</option>
                                ))}
                            </select>
                            <input
                                className="fs-7"
                                value={fieldValue(row, "pharmacyID")}
                                onChange={(e) =>
                                    setEditing((s) => ({ ...s, [row.id]: { ...s[row.id], pharmacyID: e.target.value } }))
                                }
                                style={{ padding: "6px 8px" }}
                            />
                            <input
                                readOnly
                                className="fs-7 alert-secondary"
                                value={fieldValue(row, "patientTable")}
                                onChange={(e) =>
                                    setEditing((s) => ({ ...s, [row.id]: { ...s[row.id], patientTable: e.target.value } }))
                                }
                                style={{ padding: "6px 8px" }}
                            />
                            <input
                                readOnly
                                className="fs-7 alert-secondary"
                                value={fieldValue(row, "historyTable")}
                                onChange={(e) =>
                                    setEditing((s) => ({ ...s, [row.id]: { ...s[row.id], historyTable: e.target.value } }))
                                }
                                style={{ padding: "6px 8px" }}
                            />

                            <button
                                className={saveBtnClass}
                                onClick={() => saveUser(row)}
                                style={{ padding: "6px 10px" }}
                                disabled={!edited}
                            >
                                Save
                            </button>
                        </div>
                    );
                })}

                <hr style={{ margin: "12px 0" }} />

                <div style={{ marginBottom: 8 }}>
                    <strong>Create new user</strong>
                </div>

                <div
                    className="d-grid gap-2 align-items-center"
                    style={{ gridTemplateColumns: USER_GRID_COLUMNS }}
                >
                    <input
                        className="fs-7"
                        placeholder="Username"
                        value={newUser.userName}
                        onChange={(e) => setNewUser((s) => ({ ...s, userName: e.target.value }))}
                        style={{ padding: "6px 8px" }}
                    />
                    <input
                        className="fs-7"
                        type="text"
                        placeholder="Password"
                        value={newUser.password}
                        onChange={(e) => setNewUser((s) => ({ ...s, password: e.target.value }))}
                        style={{ padding: "6px 8px" }}
                    />
                    <input
                        className="fs-7"
                        type="email"
                        placeholder="Email"
                        value={newUser.email}
                        onChange={(e) => setNewUser((s) => ({ ...s, email: e.target.value }))}
                        style={{ padding: "6px 8px" }}
                    />
                    <select
                        className="fs-7"
                        value={newUser.role}
                        onChange={(e) => setNewUser((s) => ({ ...s, role: e.target.value }))}
                        style={{ padding: "6px 8px" }}
                    >
                        {USER_ROLES.map((role) => (
                            <option key={role} value={role}>{role}</option>
                        ))}
                    </select>
                    <input
                        className="fs-7"
                        placeholder="Pharmacy ID"
                        value={newUser.pharmacyID}
                        onChange={(e) => setNewUser((s) => ({ ...s, pharmacyID: e.target.value }))}
                        style={{ padding: "6px 8px" }}
                    />
                    <input
                        className="fs-7 alert-secondary"
                        readOnly
                        placeholder="Patient Table"
                        value="Patient_DEMO"
                        onChange={(e) => setNewUser((s) => ({ ...s, patientTable: e.target.value }))}
                        style={{ padding: "6px 8px" }}
                    />
                    <input
                        readOnly
                        className="fs-7 alert-secondary"
                        placeholder="History Table"
                        value="Patient_History_DEMO"
                        onChange={(e) => setNewUser((s) => ({ ...s, historyTable: e.target.value }))}
                        style={{ padding: "6px 8px" }}
                    />
                    <button
                        className={insertBtnClass}
                        onClick={createUser}
                        disabled={!canInsert}
                        style={{ padding: "6px 10px" }}
                    >
                        Insert
                    </button>
                </div>
            </div>
        </DragBox>
    );
}
